/**
 * Seiteneinrichtung, Druckbereich und Kommentare.
 *
 * Eigener Abruf, weil `Worksheet.pageLayout` erst ab ExcelApi 1.9 und
 * `Workbook.comments` erst ab 1.10 zur Verfügung steht. Beides ist für den
 * Kurs wichtig genug, um es zu prüfen – aber neu genug, dass es in älteren
 * Excel-Versionen fehlen kann.
 */

import { excelApi, supportsExcelApi } from "./capabilities";
import { ExcelUnavailableError, isExcelAvailable, splitAddress } from "./workbook";

export interface PageLayoutInfo {
  sheetName: string;
  /** "Portrait" (Hochformat) oder "Landscape" (Querformat). */
  orientation: string | null;
  /** Festgelegter Druckbereich. `null` = die ganze benutzte Fläche wird gedruckt. */
  printArea: string | null;
  /** Zeilen, die auf jeder Seite oben wiederholt werden, z. B. "$1:$1". */
  printTitleRows: string | null;
  /** Spalten, die auf jeder Seite links wiederholt werden. */
  printTitleColumns: string | null;
  /** Auf wie viele Seiten wird die Breite gestaucht? `null` = keine Anpassung. */
  fitToWidth: number | null;
  fitToHeight: number | null;
  /** Wird der Inhalt waagerecht auf der Seite zentriert? */
  centerHorizontally: boolean | null;
}

export interface CommentInfo {
  id: string;
  /** Text des Kommentars ohne Formatierung. */
  content: string;
  /** Wer den Kommentar geschrieben hat. */
  authorName: string | null;
  /** Ist die Unterhaltung als erledigt markiert? */
  resolved: boolean | null;
  /** Anzahl der Antworten in dieser Unterhaltung. */
  replyCount: number | null;
}

export interface LayoutSnapshot {
  /** Seiteneinrichtung je Blatt. `null` vor ExcelApi 1.9. */
  pageLayouts: PageLayoutInfo[] | null;
  /** Kommentare der Arbeitsmappe. `null` vor 1.10. */
  comments: CommentInfo[] | null;
}

function value<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function text(read: () => unknown): string | null {
  const result = value(read);
  return typeof result === "string" ? result : null;
}

function flag(read: () => unknown): boolean | null {
  const result = value(read);
  return typeof result === "boolean" ? result : null;
}

function zahl(read: () => unknown): number | null {
  const result = value(read);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

/** Liest Seiteneinrichtung und Kommentare der Arbeitsmappe. */
export async function readLayout(): Promise<LayoutSnapshot> {
  if (!isExcelAvailable()) throw new ExcelUnavailableError();

  const canReadPageLayout = supportsExcelApi(excelApi.seitenlayout);
  const canReadComments = supportsExcelApi(excelApi.kommentare);
  const canReadCommentStatus = supportsExcelApi(excelApi.kommentarStatus);

  return Excel.run(async (context) => {
    const sheetCollection = context.workbook.worksheets;
    sheetCollection.load("items/name");

    const commentCollection = canReadComments ? context.workbook.comments : null;
    if (commentCollection) {
      const felder = ["items/id", "items/content", "items/authorName"];
      if (canReadCommentStatus) felder.push("items/resolved");
      commentCollection.load(felder.join(","));
    }
    await context.sync();

    const layoutRequests: Array<{
      sheetName: string;
      layout: Excel.PageLayout;
      druckbereich: Excel.RangeAreas;
      titelZeilen: Excel.Range;
      titelSpalten: Excel.Range;
    }> = [];

    if (canReadPageLayout) {
      for (const sheet of sheetCollection.items) {
        const layout = sheet.pageLayout;
        layout.load("orientation,centerHorizontally,zoom");
        const druckbereich = layout.getPrintAreaOrNullObject();
        druckbereich.load("address,isNullObject");
        const titelZeilen = layout.getPrintTitleRowsOrNullObject();
        titelZeilen.load("address,isNullObject");
        const titelSpalten = layout.getPrintTitleColumnsOrNullObject();
        titelSpalten.load("address,isNullObject");
        layoutRequests.push({
          sheetName: text(() => sheet.name) ?? "",
          layout,
          druckbereich,
          titelZeilen,
          titelSpalten,
        });
      }
    }

    const commentReplies = new Map<string, Excel.CommentReplyCollection>();
    if (commentCollection) {
      for (const comment of commentCollection.items) {
        const id = text(() => comment.id);
        if (id === null) continue;
        const replies = comment.replies;
        replies.load("items/id");
        commentReplies.set(id, replies);
      }
    }

    let gelesen = true;
    try {
      await context.sync();
    } catch {
      gelesen = false;
    }

    if (!gelesen) return { pageLayouts: null, comments: null };

    const pageLayouts: PageLayoutInfo[] | null = canReadPageLayout
      ? layoutRequests.map((request) => {
          const zoom = value(() => request.layout.zoom);
          return {
            sheetName: request.sheetName,
            orientation: text(() => request.layout.orientation),
            printArea:
              value(() => request.druckbereich.isNullObject) !== true
                ? splitAddress(text(() => request.druckbereich.address) ?? "").range || null
                : null,
            printTitleRows:
              value(() => request.titelZeilen.isNullObject) !== true
                ? splitAddress(text(() => request.titelZeilen.address) ?? "").range || null
                : null,
            printTitleColumns:
              value(() => request.titelSpalten.isNullObject) !== true
                ? splitAddress(text(() => request.titelSpalten.address) ?? "").range || null
                : null,
            fitToWidth: zahl(() => (zoom as Excel.PageLayoutZoomOptions | null)?.horizontalFitToPages),
            fitToHeight: zahl(() => (zoom as Excel.PageLayoutZoomOptions | null)?.verticalFitToPages),
            centerHorizontally: flag(() => request.layout.centerHorizontally),
          };
        })
      : null;

    const comments: CommentInfo[] | null = commentCollection
      ? commentCollection.items.map((comment) => {
          const id = text(() => comment.id) ?? "";
          const replies = commentReplies.get(id);
          const items = replies ? value(() => replies.items) : null;
          return {
            id,
            content: text(() => comment.content) ?? "",
            authorName: text(() => comment.authorName),
            resolved: flag(() => comment.resolved),
            replyCount: Array.isArray(items) ? items.length : null,
          };
        })
      : null;

    return { pageLayouts, comments };
  });
}

/* ---------- Auswertung ---------- */

/** Seiteneinrichtung eines bestimmten Blattes. */
export function layoutFuerBlatt(snapshot: LayoutSnapshot, sheetName: string): PageLayoutInfo | null {
  const gesucht = sheetName.trim().toLowerCase();
  return (
    (snapshot.pageLayouts ?? []).find(
      (eintrag) => eintrag.sheetName.trim().toLowerCase() === gesucht,
    ) ?? null
  );
}

/** Blätter, auf denen die Kopfzeile beim Drucken wiederholt wird. */
export function blaetterMitWiederholungszeile(snapshot: LayoutSnapshot): PageLayoutInfo[] {
  return (snapshot.pageLayouts ?? []).filter((eintrag) => eintrag.printTitleRows !== null);
}

/** Blätter, für die die Ausgabe auf eine Seitenbreite gestaucht wird. */
export function blaetterMitSeitenanpassung(snapshot: LayoutSnapshot): PageLayoutInfo[] {
  return (snapshot.pageLayouts ?? []).filter(
    (eintrag) => eintrag.fitToWidth !== null && eintrag.fitToWidth > 0,
  );
}

/** Noch offene, also nicht als erledigt markierte Kommentare. */
export function offeneKommentare(snapshot: LayoutSnapshot): CommentInfo[] {
  return (snapshot.comments ?? []).filter((eintrag) => eintrag.resolved !== true);
}
